import React from "react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Section, SectionLabel, SectionTitle } from "@/components/layout/Section";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cpu, Blocks, Sparkles, Boxes, Workflow, Compass } from "lucide-react";
import { HOME } from "@/constants/testIds";

const whatWeDo = [
  {
    title: "Custom Software",
    body: "Purpose-built software designed around the unique needs, workflows, and goals of the organizations we serve. Every solution is intentionally crafted to improve efficiency, eliminate friction, and create long-term value.",
  },
  {
    title: "Intelligent Systems",
    body: "Design and implement intelligent systems that connect people, processes, and technology through thoughtful automation, strategic integrations, APIs, and scalable digital infrastructure.",
  },
  {
    title: "Digital Transformation",
    body: "Help organizations modernize operations, streamline decision-making, and embrace technology with confidence through practical, human-centered digital transformation strategies.",
  },
];

const thoughtful = [
  {
    title: "Systems First",
    body: "Every successful solution begins by understanding how people, processes, and technology work together before writing a single line of code.",
  },
  {
    title: "Built with Intention",
    body: "Every feature should solve a meaningful problem. Simplicity, clarity, and purpose always take precedence over unnecessary complexity.",
  },
  {
    title: "Practical Intelligence",
    body: "Innovation is only valuable when it improves real-world outcomes. We build technology that people can understand, adopt, and depend on.",
  },
];

const capabilities = [
  { icon: Cpu, title: "Custom Software", body: "Purpose-built applications designed around the unique needs of your organization." },
  { icon: Blocks, title: "Intelligent Systems", body: "Connected technologies that help organizations automate processes, improve visibility, and make more informed decisions." },
  { icon: Boxes, title: "Strategic Integrations", body: "Connect existing platforms, data, and workflows to create a more unified operational experience." },
  { icon: Sparkles, title: "Artificial Intelligence", body: "Practical AI experiences that enhance productivity, reduce repetitive work, and support better decision-making." },
  { icon: Workflow, title: "Workflow Automation", body: "Eliminate unnecessary manual tasks through intelligent automation and streamlined digital processes." },
  { icon: Compass, title: "Technology Consulting", body: "Strategic guidance that helps organizations evaluate opportunities, modernize operations, and make confident technology decisions." },
];

const whyBrp = [
  {
    title: "Thoughtful Partnerships",
    body: "Every successful engagement begins with listening. Before recommending technology, we take the time to understand your people, your goals, and the operational realities that make your organization unique.",
  },
  {
    title: "Modern Intelligent Solutions",
    body: "From custom software and intelligent systems to thoughtfully designed AI solutions, workflow automation, API development, and strategic integrations, every solution is intentionally crafted to simplify operations, strengthen decision-making, and help organizations work with greater confidence every day.",
  },
  {
    title: "Built for Long-Term Growth",
    body: "Technology should evolve alongside your organization. We create scalable, adaptable solutions that continue delivering value as your needs grow and change.",
  },
  {
    title: "Practical Innovation",
    body: "Innovation isn’t about chasing trends. It’s about implementing the right technology in the right places to eliminate unnecessary friction, improve efficiency, and create measurable business outcomes.",
  },
  {
    title: "Quality Without Compromise",
    body: "Every interaction, workflow, and user experience reflects our commitment to thoughtful execution. We believe exceptional technology is built through attention to detail, not unnecessary complexity.",
  },
  {
    title: "Relationships That Last",
    body: "We’re committed to becoming a trusted technology partner—not simply completing a project. Our success is measured by the long-term value we create for the organizations we serve.",
  },
];

export default function HomePage() {
  return (
    <SiteLayout headerVariant="home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Editorial hero photograph */}
        <div aria-hidden className="absolute inset-0">
          <picture>
            <source srcSet="/hero-home.webp" type="image/webp" />
            <img
              src="/hero-home.jpg"
              alt=""
              className="h-full w-full object-cover object-center"
              loading="eager"
              fetchpriority="high"
            />
          </picture>
          {/* Editorial gradient: strong on left where copy sits, feathered on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--background))] via-[hsl(var(--background))]/85 to-[hsl(var(--background))]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-transparent to-[hsl(var(--background))]/40" />
          <div className="absolute inset-0 brp-hero-glow opacity-70" />
          <div className="absolute inset-0 brp-hero-glow-orange opacity-50" />
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 pt-16 pb-24 sm:pt-24 sm:pb-32 lg:pt-32 lg:pb-40">
          <Reveal>
            <div className="text-[11px] font-medium tracking-[0.28em] uppercase text-[hsl(var(--brp-orange))] mb-6">
              Brilliant Regular People — Tech
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-semibold tracking-[-0.03em] text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
              Technology that eliminates operational friction and creates lasting impact.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              We design and deliver human-centered technology solutions through custom software, intelligent systems, strategic integrations, and digital transformation — tailored to the organizations we serve.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button
                  size="lg"
                  data-testid={HOME.heroPrimaryCta}
                  className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-7 h-12 text-sm tracking-wide"
                >
                  Let’s Talk <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid={HOME.heroSecondaryCta}
                  className="rounded-full border-border/70 text-[hsl(var(--brp-ink))] hover:bg-white/5 px-7 h-12 text-sm tracking-wide backdrop-blur"
                >
                  Explore Solutions
                </Button>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-24 sm:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="text-[11px] font-medium tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))] mb-4">Core Message</div>
                <p className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-[-0.02em]">
                  While Artificial Intelligence is amazing,<br />
                  <span className="text-[hsl(var(--brp-orange))]">Authentic Intelligence</span> built it.
                </p>
              </div>
              <div className="lg:col-span-7 space-y-4 text-[hsl(var(--brp-ink-dim))] text-base leading-relaxed">
                <p>We believe every meaningful innovation begins with a human being who cared enough to solve a problem. That’s who we are.</p>
                <p>We’re not the out-of-touch tech heads. We’re the very human, very logical, very passionate problem solvers who happen to enjoy creating systems, software, intelligent workflows, APIs, automations, and digital tools that help people thrive.</p>
                <p className="text-[hsl(var(--brp-ink))]">Technology is our medium. People are our purpose.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE DO */}
      <Section tone="paper" py="lg">
        <Reveal>
          <SectionLabel className="!text-[hsl(var(--brp-orange))]">What We Do</SectionLabel>
          <SectionTitle className="mt-4 max-w-3xl" size="xl">Purposeful technology, delivered with intention.</SectionTitle>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {whatWeDo.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.05}>
              <div className="pt-8 border-t border-[hsl(var(--brp-border-on-paper))]">
                <div className="font-mono text-xs tracking-widest text-[hsl(var(--brp-orange))]">0{i + 1}</div>
                <h3 className="mt-4 text-2xl font-display font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-4 text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* THOUGHTFUL BY DESIGN */}
      <Section tone="dark" py="lg">
        <Reveal>
          <SectionLabel tone="blue">Thoughtful by Design</SectionLabel>
          <SectionTitle className="mt-4 max-w-3xl" size="xl">
            Technology should never be the starting point.<br />
            <span className="text-[hsl(var(--brp-ink-dim))]">Understanding the system is.</span>
          </SectionTitle>
        </Reveal>

        <Reveal delay={0.1}>
          <figure className="mt-12 relative overflow-hidden rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))]">
            <picture>
              <source srcSet="/thoughtful-by-design.webp" type="image/webp" />
              <img
                src="/thoughtful-by-design.jpg"
                alt="Human intent flowing into a BRP Tech workflow system — thoughtful design in motion"
                loading="lazy"
                className="block w-full h-auto"
              />
            </picture>
            <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <figcaption className="absolute left-5 sm:left-8 bottom-4 sm:bottom-6 right-5 sm:right-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[hsl(var(--brp-orange))]">
                <span className="inline-flex items-center gap-2"><span className="h-px w-6 bg-[hsl(var(--brp-orange))]" /> A System, Considered</span>
              </div>
              <div className="text-[11px] italic text-white/70 font-light max-w-md sm:text-right">
                Every workflow begins as a human intention, refined by understanding, and expressed as clarity.
              </div>
            </figcaption>
          </figure>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {thoughtful.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.05}>
              <div className="pt-6 border-t border-border/70">
                <div className="font-mono text-xs tracking-widest text-[hsl(var(--brp-blue))]">0{i + 1}</div>
                <h3 className="mt-3 text-xl font-display font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* HOW WE DELIVER */}
      <Section tone="dark" py="lg">
        <Reveal>
          <SectionLabel tone="orange">How We Deliver</SectionLabel>
        </Reveal>

        <Reveal delay={0.08}>
          <figure className="mt-6 relative overflow-hidden rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))]">
            <picture>
              <source srcSet="/how-we-deliver.webp" type="image/webp" />
              <img
                src="/how-we-deliver.jpg"
                alt="BRP Tech bridges scattered systems, data, and processes into intuitive solutions people love to use"
                loading="lazy"
                className="block w-full h-auto"
              />
            </picture>
          </figure>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-6">
              <SectionTitle size="xl" className="max-w-xl">
                Designed for today’s challenges.<br />
                Built for tomorrow’s opportunities.
              </SectionTitle>
            </div>
            <p className="lg:col-span-6 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              Every engagement is different, but our approach is grounded in the same objective: delivering thoughtful technology that helps organizations operate with greater clarity, efficiency, and confidence. Our work spans multiple disciplines, allowing us to build solutions that are as flexible as the organizations they support.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.04}>
              <div
                data-testid="capability-card"
                className="group h-full rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-6 hover:bg-[hsl(var(--brp-surface-3))] hover:border-[hsl(var(--brp-blue))]/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-blue))]">
                    <c.icon className="h-4 w-4" />
                  </div>
                  <h3 className="text-base font-display font-semibold tracking-tight">{c.title}</h3>
                </div>
                <p className="mt-4 text-sm text-[hsl(var(--brp-ink-dim))] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* WHY BRP TECH */}
      <Section tone="paper" py="lg">
        <Reveal>
          <SectionLabel className="!text-[hsl(var(--brp-orange))]">Why BRP Tech</SectionLabel>
          <SectionTitle className="mt-4 max-w-4xl" size="xl">
            The right strategy, the right systems,<br />the right partner.
          </SectionTitle>
          <p className="mt-6 max-w-3xl text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">
            Technology alone doesn’t transform organizations. At BRP Tech, every engagement begins with understanding how your organization operates today while designing thoughtful technology solutions that prepare you for tomorrow.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-10">
          {whyBrp.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.03}>
              <div data-testid="why-brp-block" className="pt-6 border-t border-[hsl(var(--brp-border-on-paper))]">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs tracking-widest text-[hsl(var(--brp-orange))]">0{i + 1}</span>
                  <h3 className="text-xl font-display font-semibold tracking-tight">{b.title}</h3>
                </div>
                <p className="mt-3 text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* OUR PHILOSOPHY */}
      <Section tone="dark" py="lg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionLabel tone="blue">Our Philosophy</SectionLabel>
            <SectionTitle className="mt-4" size="xl">
              Technology should create confidence — not confusion.
            </SectionTitle>
          </Reveal>
          <Reveal className="lg:col-span-7 space-y-6 text-[hsl(var(--brp-ink-dim))] leading-relaxed" delay={0.1}>
            <p>The best technology isn’t the most complicated. It’s the technology that quietly removes obstacles, simplifies everyday work, and allows people to focus on what matters most.</p>
            <p>At BRP Tech, we believe innovation should feel approachable, practical, and purposeful. Every solution begins with thoughtful strategy, is refined through modern engineering, and is delivered with the people who use it at the center of every decision.</p>
            <p>By weaving state-of-the-art software, intelligent systems, thoughtfully designed AI solutions, workflow automation, API development, and modern digital infrastructure directly into an organization’s operations, we help create environments where work feels more connected, decisions become more informed, and daily operations become noticeably more efficient.</p>
            <p className="text-[hsl(var(--brp-ink))]">When technology is implemented with intention, it no longer feels like another system to manage. It becomes a natural extension of the organization itself.</p>
          </Reveal>
        </div>

        <div className="mt-20 flex flex-col items-center text-center gap-6">
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-[-0.02em] max-w-2xl">
            Every meaningful innovation begins with a conversation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-8 h-12">
              Let’s Talk <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>
    </SiteLayout>
  );
}
